import { useCallback, useEffect, useState } from "react";
import { FileText, Download, Loader2, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const money = (n) => `$${(n || 0).toFixed(2)}`;
const nzDate = (ymd) => (ymd ? new Date(ymd + "T00:00:00").toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }) : "");

const saveCsv = async (inv) => {
  const blob = await api.downloadXeroCsv(inv.id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${inv.invoice_number}-xero.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const InvoiceRow = ({ inv, onCancelled }) => {
  const { user } = useAuth();
  const cancel = async () => {
    if (!window.confirm(`Cancel invoice ${inv.invoice_number} for ${money(inv.total)}?\n\nOnly do this if it was never sent to the tenant or imported into Xero. Its readings will be unlocked and can be invoiced again.`)) return;
    try { await api.adminCancelInvoice(inv.id); toast.success(`Invoice ${inv.invoice_number} cancelled`); onCancelled?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel invoice"); }
  };
  return (
  <div className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-50 text-xs" data-testid={`invoice-row-${inv.id}`}>
    <div className="min-w-0">
      <p className="font-semibold text-slate-800">{inv.invoice_number} <span className="font-normal text-slate-500">· {nzDate(inv.invoice_date)}</span></p>
      <p className="text-slate-500 truncate">{inv.contact_name} · by {inv.created_by}</p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <span className="font-mono-nums font-semibold text-slate-900">{money(inv.total)}</span>
      <button onClick={() => saveCsv(inv).catch(() => toast.error("Could not download Xero file"))} className="text-blue-600 hover:text-blue-800" title="Download Xero file" data-testid={`invoice-download-${inv.id}`}>
        <Download className="h-3.5 w-3.5" />
      </button>
      {user?.role === "admin" && (
        <button onClick={cancel} className="rounded border border-red-200 px-1.5 py-0.5 text-[10px] font-semibold text-red-600 hover:bg-red-50" data-testid={`invoice-cancel-${inv.id}`}>Cancel invoice</button>
      )}
    </div>
  </div>
  );
};

export const UtilityBilling = ({ unitId, refreshKey, onInvoiced }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.getBilling(unitId).then(setData).catch(() => setData(null)); }, [unitId]);
  useEffect(() => { if (unitId) { load(); } }, [unitId, refreshKey, load]);

  const invoice = async () => {
    if (!window.confirm(`Create an invoice for ${money(data.unbilled.total)} and download the Xero file? The running balance will reset to $0.`)) return;
    setBusy(true);
    try {
      const inv = await api.createInvoice(unitId);
      await saveCsv(inv);
      toast.success(`Invoice ${inv.invoice_number} created — import the file into Xero`);
      load();
      onInvoiced?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create invoice");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const { unbilled, invoices, lifetime_total } = data;
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="utility-billing">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <Receipt className="h-4 w-4 text-emerald-600" />
        <h4 className="font-semibold text-sm text-slate-800">Billing</h4>
        <span className="ml-auto text-xs text-slate-500">Total invoiced to date <b className="font-mono-nums text-slate-900" data-testid="billing-lifetime-total">{money(lifetime_total)}</b></span>
      </div>
      <div className="p-3 space-y-3">
        <div className="rounded-md bg-emerald-50/60 border border-emerald-100 p-3 text-xs space-y-1">
          <p className="font-medium text-slate-600">Not yet invoiced</p>
          {unbilled.lines.map((ln) => (
            <div key={ln.meter_type} className="flex justify-between font-mono-nums" data-testid={`billing-unbilled-${ln.meter_type}`}>
              <span><span className="capitalize">{ln.meter_type}</span>: {ln.quantity} {ln.unit} × ${ln.rate}</span>
              <span>{money(ln.amount)}</span>
            </div>
          ))}
          <div className="flex justify-between font-bold text-sm text-slate-900 pt-1 border-t border-emerald-100">
            <span>Balance to invoice</span><span data-testid="billing-unbilled-total">{money(unbilled.total)}</span>
          </div>
        </div>
        <Button onClick={invoice} disabled={busy || unbilled.total <= 0} className="w-full h-9 gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="billing-create-invoice-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Invoice &amp; download Xero file
        </Button>
        {invoices.length > 0 && (
          <div className="max-h-40 overflow-auto" data-testid="billing-invoice-list">
            {invoices.map((inv) => <InvoiceRow key={inv.id} inv={inv} onCancelled={() => { load(); onInvoiced?.(); }} />)}
          </div>
        )}
      </div>
    </div>
  );
};
